import React from "react";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useCart } from "../../context/CartContext";
import EnquiryCartForm from "./EnquiryCartForm";
import productData from '../../data/products.json';

function QuantityControls({ id, quantity, updateQuantity, removeFromCart, size = "desktop" }) {
  const btnClass = size === "mobile"
    ? "bg-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300 font-bold text-lg"
    : "bg-gray-200 px-2 py-1 rounded hover:bg-gray-300 font-bold";
  return (
    <div className="flex items-center justify-center gap-2">
      <button onClick={() => updateQuantity(id, -1)} className={btnClass} aria-label="Decrease quantity">-</button>
      <span className={size === "mobile" ? "font-medium text-base min-w-[32px] text-center" : ""}>{quantity}</span>
      <button onClick={() => updateQuantity(id, 1)} className={btnClass} aria-label="Increase quantity">+</button>
      <button onClick={() => removeFromCart(id)} className={size === "mobile" ? "text-red-500 hover:text-red-700 text-2xl p-2 rounded-full bg-white shadow-sm ml-2" : "text-red-500 hover:text-red-700 text-xl"} aria-label="Remove item">
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className={size === "mobile" ? "w-7 h-7" : "w-6 h-6"}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M8 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
  </svg>
</button>
    </div>
  );
}

export default function CartPage() {
  const { cart, removeFromCart, updateQuantity, clearCart } = useCart();

  // Build product map for O(1) lookup
  const productMap = React.useMemo(() => {
    const map = {};
    productData.products.forEach(p => { map[p.id] = p; });
    return map;
  }, []);

  const meta = {
  title: 'Enquiry Cart | Bagga International',
  meta: [
    { name: 'description', content: 'View and manage your enquiry cart at Bagga International.' },
    { property: 'og:title', content: 'Enquiry Cart | Bagga International' },
    { property: 'og:description', content: 'View and manage your enquiry cart at Bagga International.' },
    { property: 'og:image', content: '/images/site-default-og.jpg' },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: 'https://baggainternational.com/cart' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Enquiry Cart | Bagga International' },
    { name: 'twitter:description', content: 'View and manage your enquiry cart at Bagga International.' },
    { name: 'twitter:image', content: '/images/site-default-og.jpg' }
  ],
  link: [
    { rel: 'canonical', href: 'https://baggainternational.com/cart' }
  ],
  style: [],
  script: [],
};

return (
    <>
      <HelmetProvider>
        <Helmet {...meta}></Helmet>
      </HelmetProvider>
      <div className="container mx-auto px-4 sm:px-12 md:px-20 py-8 w-full max-w-full overflow-x-hidden box-border flex flex-col gap-6" style={{ maxWidth: '100vw' }}>
        <h1 className="text-3xl font-bold mb-8 text-center">Enquiry Cart</h1>
        {cart.length === 0 ? (
          <p className="text-gray-500 text-center" style={{ margin: '40px 0' }}>Your cart is empty.</p>
        ) : (
          <div className="mb-8">
            <div className="hidden sm:block">
              <table className="w-full border-collapse mb-6 bg-white rounded-lg shadow">
                <thead>
                  <tr className="bg-gray-100 text-gray-800 text-left">
                    <th className="p-3">#</th>
                    <th className="p-3">Image</th>
                    <th className="p-3">Product Name</th>
                    <th className="p-3">Product Code</th>
                    <th className="p-3 text-center">Quantity</th>
                    <th className="p-3 text-center">Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {cart.map((item, idx) => {
                    const product = productMap[item.id];
                    return (
                      <tr key={item.id} className="border-b hover:bg-gray-50">
                        <td className="p-3">{idx + 1}</td>
                        <td className="p-3">
                          <img src={item.image} alt={item.name} style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }} />
                        </td>
                        <td className="p-3 font-semibold">
                          <a href={`/categories/${product?.categoryId || 'unknown'}/${item.id}`} className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">{item.name}</a>
                        </td>
                        <td className="p-3">{item.code}</td>
                        <td className="p-3 text-center">
                          <QuantityControls id={item.id} quantity={item.quantity} updateQuantity={updateQuantity} removeFromCart={removeFromCart} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Mobile card layout */}
            <div className="sm:hidden space-y-4 mb-6">
              {cart.map((item, idx) => {
                const product = productMap[item.id];
                return (
                  <div
                    key={item.id}
                    className="relative bg-white border border-gray-200 rounded-lg shadow p-4 mb-2 flex flex-col w-full"
                  >
                    {/* Remove button top right */}
                    <div className="flex gap-4 items-start w-full">
                      {/* Product image */}
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-20 h-20 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                      />
                      {/* Info */}
                      <div className="flex-1 min-w-0 flex flex-col justify-center">
                        <div className="font-medium text-base text-gray-800 break-words line-clamp-2 mb-1">
                          <a href={`/categories/${product?.categoryId || 'unknown'}/${item.id}`} className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">{item.name}</a>
                        </div>
                        {item.code && (
                          <div className="text-xs text-gray-500 mb-2">Code: {item.code}</div>
                        )}
                        <QuantityControls id={item.id} quantity={item.quantity} updateQuantity={updateQuantity} removeFromCart={removeFromCart} size="mobile" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between items-center mb-4">
              <span className="font-medium text-gray-700">Total Items: <span className="font-bold">{cart.reduce((sum, item) => sum + item.quantity, 0)}</span></span>
              <button onClick={clearCart} className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-4 py-2 rounded">Clear Cart</button>
            </div>
          </div>
        )}
        {/* Enquiry Form */}
        <EnquiryCartForm />
      </div>
    </>
  );
}
